/**
 * Agent-Kurzstatus für die Dashboard-Server-Cards — reine, DOM-freie Logik.
 * Spiegelt die Antwort der read-only Action `agent_server_status_summary`
 * (server-seitig `src/agent/status-summary.ts`) und bietet einen kleinen Indexer
 * (server_id → Summary). Formatter (Prozent/Uptime/Uhrzeit) werden aus
 * `agent-live.ts` WIEDERVERWENDET — hier nichts dupliziert.
 */

export type AgentCardStatus = "online" | "stale" | "offline" | "unknown";

export interface AgentServerStatusSummary {
  server_id: string;
  status: AgentCardStatus;
  age_seconds: number | null;
  last_heartbeat: number | null;
  agent_version: string | null;
  queue: number | null;
  cpu_percent: number | null;
  memory_percent: number | null;
  disk_percent: number | null;
  uptime_seconds: number | null;
}

const VALID_STATUS: readonly AgentCardStatus[] = ["online", "stale", "offline", "unknown"];

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

/**
 * Normalisiert eine rohe Summary-Zeile der Action defensiv (fehlende/kaputte
 * Felder → `null`, unbekannter Status → `unknown`). `null`, wenn keine
 * `server_id` erkennbar ist.
 */
export function normalizeAgentSummary(raw: unknown): AgentServerStatusSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const serverId = str(o.server_id);
  if (!serverId) return null;
  const status = VALID_STATUS.includes(o.status as AgentCardStatus)
    ? (o.status as AgentCardStatus)
    : "unknown";
  return {
    server_id: serverId,
    status,
    age_seconds: num(o.age_seconds),
    last_heartbeat: num(o.last_heartbeat),
    agent_version: str(o.agent_version),
    queue: num(o.queue),
    cpu_percent: num(o.cpu_percent),
    memory_percent: num(o.memory_percent),
    disk_percent: num(o.disk_percent),
    uptime_seconds: num(o.uptime_seconds),
  };
}

/**
 * Baut aus der Action-Antwort eine `server_id → Summary`-Map für den schnellen
 * Zugriff je Card. Defensiv gegen Nicht-Array/kaputte Zeilen.
 */
export function indexAgentSummaries(list: unknown): Record<string, AgentServerStatusSummary> {
  const out: Record<string, AgentServerStatusSummary> = {};
  if (!Array.isArray(list)) return out;
  for (const raw of list) {
    const s = normalizeAgentSummary(raw);
    if (s) out[s.server_id] = s;
  }
  return out;
}
